/**
 * Auth is the one piece of state that must survive page reloads and be read
 * synchronously before any API call. So it lives in Zustand with persistence.
 * Everything else (listings, subscriptions, admin) uses TanStack Query.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { capture, identifyUser, resetAnalytics } from '@/lib/analytics'
import { extractApiStatus } from '@/lib/apiError'
import { configureApiClient } from '@/lib/apiClient'
import { queryClient } from '@/lib/queryClient'
import { isPublicPath } from '@/router/publicPaths'
import {
  fetchCurrentSubscriber,
  logout as apiLogout,
  magicLogin as apiMagicLogin,
  refreshSession,
  verifyCode as apiVerifyCode,
} from './api'
import type { AuthSession, Subscriber } from './types'

interface AuthState {
  user: Subscriber | null
  accessToken: string | null
  refreshToken: string | null
  isInitialized: boolean
  isLoading: boolean

  verifyCode: (email: string, code: string) => Promise<void>
  magicLogin: (token: string) => Promise<void>
  logout: () => Promise<void>
  initialize: () => Promise<void>
  setSession: (session: AuthSession) => void
  setUser: (user: Subscriber) => void
  clear: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isInitialized: false,
      isLoading: false,

      setSession(session) {
        set({
          user: session.subscriber,
          accessToken: session.tokens.accessToken,
          refreshToken: session.tokens.refreshToken,
        })
        identifyUser(session.subscriber)
      },

      setUser(user) {
        set({ user })
        identifyUser(user)
      },

      clear() {
        const wasAuthenticated = Boolean(get().user)
        resetAnalytics()
        set({ user: null, accessToken: null, refreshToken: null })
        queryClient.clear()
        // Hard-redirect to /login when we were previously authenticated and
        // are on a protected route. Public pages (token links included) stay
        // put. Survives any stale React tree.
        if (
          wasAuthenticated &&
          typeof window !== 'undefined' &&
          !isPublicPath(window.location.pathname)
        ) {
          window.location.assign('/login')
        }
      },

      async verifyCode(email, code) {
        set({ isLoading: true })
        try {
          const session = await apiVerifyCode(email, code)
          get().setSession(session)
          capture('login_completed', { method: 'otp' })
        } finally {
          set({ isLoading: false })
        }
      },

      async magicLogin(token) {
        set({ isLoading: true })
        try {
          const session = await apiMagicLogin(token)
          get().setSession(session)
          capture('login_completed', { method: 'magic_link' })
        } finally {
          set({ isLoading: false })
        }
      },

      async logout() {
        const refreshToken = get().refreshToken
        try {
          if (refreshToken) await apiLogout(refreshToken)
        } catch {
          // best-effort
        }
        get().clear()
      },

      async initialize() {
        const { accessToken } = get()
        if (!accessToken) {
          set({ isInitialized: true })
          return
        }
        try {
          const user = await fetchCurrentSubscriber()
          set({ user, isInitialized: true })
        } catch (err) {
          // Seul un 401 prouve que la session est invalide. Réseau, timeout ou
          // 5xx : on garde l'utilisateur et les jetons persistés, les requêtes
          // suivantes retenteront.
          if (extractApiStatus(err) === 401) get().clear()
          set({ isInitialized: true })
        }
      },
    }),
    {
      name: 'alerte-immo:auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
)

// Wire the API client to the store. Keeps auth out of apiClient.ts.
configureApiClient({
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  setTokens: (accessToken, refreshToken) => {
    const current = useAuthStore.getState()
    if (current.user) {
      useAuthStore.setState({ accessToken, refreshToken })
    }
  },
  onLogout: () => {
    useAuthStore.getState().clear()
  },
  executeRefresh: async (refreshToken) => {
    const session = await refreshSession(refreshToken)
    useAuthStore.getState().setSession(session)
    return {
      accessToken: session.tokens.accessToken,
      refreshToken: session.tokens.refreshToken,
    }
  },
  // Before refreshing, re-read localStorage so a rotation done by another tab
  // is reflected in memory (this tab reads tokens from in-memory state).
  syncTokensFromStorage: () => useAuthStore.persist.rehydrate(),
})

// Cross-tab token sync. Zustand's persist hydrates from localStorage only at
// load; without this, a tab left open keeps a stale refresh token in memory and,
// on its next 401, replays a token another tab already consumed — the backend
// then revokes the whole family and logs everyone out. Re-hydrating on the
// `storage` event keeps every tab on the latest rotated pair (and propagates
// logout, which nulls the tokens everywhere).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'alerte-immo:auth') {
      void useAuthStore.persist.rehydrate()
    }
  })
}

export const selectIsAuthenticated = (s: AuthState): boolean =>
  Boolean(s.user && s.accessToken)
export const selectIsAdmin = (s: AuthState): boolean =>
  s.user?.role === 'admin'
